/**
 * Notifications feed for the desktop DetailPanel.
 *
 * Mirrors the rendering of `UnifiedNotificationsSection` in `header.tsx` (views,
 * mentions, upvotes, wall comments unified by date) but is panel-friendly:
 * no big "Feeds" title and routes user clicks through a caller-provided
 * `onUserClick` callback (so the host can open the profile inside the same
 * DetailPanel via `open({ kind: 'profile', id })`).
 *
 * Reuses the same React Query keys as the mobile header so the cache is shared
 * — no duplicate network requests when both surfaces are mounted.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { getToken } from '@/lib/token-storage';
import { getProfileViewers } from '@/lib/profile-views-api';
import TelegramImage from '@/components/profile/telegram-image';
import { VipBadge } from '@/components/vip-badge';
import { translations } from '@/lib/translations';

// localStorage key + custom event used to coordinate "feed has been viewed"
// between the top-bar bell and the unread-count hook.
const FEED_LAST_VIEWED_KEY = 'kg_feed_notifications_last_viewed_at';
const FEED_VIEWED_EVENT = 'kg-feed-notifications-viewed';

export function markFeedNotificationsViewed(): void {
  try {
    const ts = Date.now();
    localStorage.setItem(FEED_LAST_VIEWED_KEY, String(ts));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(FEED_VIEWED_EVENT, { detail: { ts } }));
    }
  } catch {
    // localStorage may be blocked; the hook still works but per-tab only
  }
}

function useFeedLastViewedAt(): number {
  const [ts, setTs] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(FEED_LAST_VIEWED_KEY);
      return stored ? parseInt(stored, 10) || 0 : 0;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ ts: number }>).detail;
      if (detail?.ts) setTs(detail.ts);
    };
    window.addEventListener(FEED_VIEWED_EVENT, handler);
    return () => window.removeEventListener(FEED_VIEWED_EVENT, handler);
  }, []);
  return ts;
}

interface NotificationsFeedPanelProps {
  onUserClick: (userId: number) => void;
}

const getCategoryColor = (category: string | null) => {
  if (!category) return 'text-white/80';
  switch (category) {
    case 'Couple':
      return 'text-white/80';
    case 'Lady':
      return 'text-pink-400';
    case 'Gentleman':
      return 'text-blue-500';
    case 'Dom (M)':
    case 'Sub (M)':
    case 'Switch (M)':
    case 'Gay':
    case 'Bisexual (M)':
    case 'Lover (M)':
      return 'text-blue-500';
    case 'Dom (F)':
    case 'Sub (F)':
    case 'Switch (F)':
    case 'Lesbian':
    case 'Bisexual (F)':
    case 'Lover (F)':
      return 'text-pink-400';
    case 'Transgender':
      return 'text-purple-400';
    default:
      return 'text-white/80';
  }
};

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  if (diffSec < 60) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHour < 24) return `${diffHour}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
};

/**
 * Lightweight hook that reads the same React-Query caches the feed panel
 * populates and reports whether anything is unseen. Used by the desktop top
 * bar to decide whether the megaphone should be static or animated with the
 * red dot.
 *
 * "Unseen" is approximated by the total notification count for now (no
 * dedicated per-user lastSeen timestamp). When zero → static icon, no dot.
 */
export function useFeedNotificationsCount(): number {
  const { user } = useAuth();
  const enabled = !!user?.id;
  const { data: viewsData } = useQuery({
    queryKey: ['/api/profile-views/recent'],
    queryFn: async () => {
      try {
        return (await getProfileViewers()) || [];
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });
  const { data: commentsData } = useQuery({
    queryKey: ['/api/wall/comments/received'],
    queryFn: async () => {
      try {
        const r = await fetch('/api/wall/comments/received', {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!r.ok) throw new Error('fail');
        return await r.json();
      } catch {
        return { comments: [] };
      }
    },
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });
  const { data: mentionsData } = useQuery({
    queryKey: ['/api/wall/mentions'],
    queryFn: async () => {
      try {
        const r = await fetch('/api/wall/mentions', {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!r.ok) throw new Error('fail');
        return await r.json();
      } catch {
        return { mentions: [] };
      }
    },
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });
  const { data: upvotesData } = useQuery({
    queryKey: ['/api/wall/upvotes/received'],
    queryFn: async () => {
      try {
        const r = await fetch('/api/wall/upvotes/received', {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!r.ok) throw new Error('fail');
        return await r.json();
      } catch {
        return { upvotes: [] };
      }
    },
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });

  const lastViewedAt = useFeedLastViewedAt();
  const countNew = (items: any[] | undefined, dateFields: string[]) => {
    if (!items || items.length === 0) return 0;
    return items.filter((it) => {
      // Pick the first non-empty date field — matches the mobile render that
      // does `new Date(m.mentionedAt || m.postCreatedAt)` etc.
      let raw: any = null;
      for (const f of dateFields) {
        if (it?.[f]) { raw = it[f]; break; }
      }
      if (!raw) return false; // no usable date → don't count as unread
      const t = new Date(raw).getTime();
      if (!Number.isFinite(t)) return false;
      // First-time visitors (no stored timestamp) see everything as unread.
      if (!lastViewedAt) return true;
      return t > lastViewedAt;
    }).length;
  };
  const views = countNew(viewsData as any[], ['viewedAt']);
  const comments = countNew((commentsData as any)?.comments, ['createdAt']);
  const mentions = countNew((mentionsData as any)?.mentions, ['mentionedAt', 'postCreatedAt']);
  const upvotes = countNew((upvotesData as any)?.upvotes, ['votedAt']);
  return views + comments + mentions + upvotes;
}

export function NotificationsFeedPanel({ onUserClick }: NotificationsFeedPanelProps) {
  const { user } = useAuth();
  const validLanguages = ['en', 'it', 'es', 'de', 'pt', 'fr'];
  const userLanguage = (validLanguages.includes(user?.language || '')
    ? user?.language
    : 'en') as keyof typeof translations;
  const t = translations[userLanguage] || translations.en;

  const { data: viewsData, isLoading: loadingViews } = useQuery({
    queryKey: ['/api/profile-views/recent'],
    queryFn: async () => {
      try {
        return (await getProfileViewers()) || [];
      } catch {
        return [];
      }
    },
    enabled: !!user?.id,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });

  const { data: commentsData, isLoading: loadingComments } = useQuery({
    queryKey: ['/api/wall/comments/received'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/wall/comments/received', {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Failed to fetch comments');
        return await response.json();
      } catch {
        return { comments: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });

  const { data: mentionsData, isLoading: loadingMentions } = useQuery({
    queryKey: ['/api/wall/mentions'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/wall/mentions', {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Failed to fetch mentions');
        return await response.json();
      } catch {
        return { mentions: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });

  const { data: upvotesData, isLoading: loadingUpvotes } = useQuery({
    queryKey: ['/api/wall/upvotes/received'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/wall/upvotes/received', {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Failed to fetch upvotes');
        return await response.json();
      } catch {
        return { upvotes: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });

  const views = (viewsData as any) || [];
  const mentions = (mentionsData as any)?.mentions || [];
  const upvotes = (upvotesData as any)?.upvotes || [];
  const comments = (commentsData as any)?.comments || [];

  const unifiedNotifications = useMemo(() => {
    const combined: any[] = [
      ...views.map((v: any) => ({ ...v, notificationType: 'view', date: new Date(v.viewedAt) })),
      ...mentions.map((m: any) => ({ ...m, notificationType: 'mention', date: new Date(m.mentionedAt || m.postCreatedAt) })),
      ...upvotes.map((u: any) => ({ ...u, notificationType: 'upvote', date: new Date(u.votedAt) })),
      ...comments.map((c: any) => ({ ...c, notificationType: 'comment', date: new Date(c.createdAt) })),
    ];
    return combined.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [views, mentions, upvotes, comments]);

  const isLoading = loadingViews || loadingMentions || loadingUpvotes || loadingComments;

  if (isLoading && unifiedNotifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (unifiedNotifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <img
          src="/assets/emoji/megaphone.webp"
          alt="📣"
          className="w-16 h-16 mx-auto"
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            filter: 'drop-shadow(0 0 2px rgba(37, 99, 235, 0.3))',
          }}
        />
        <h3 className="font-medium text-[#f5f5f5] text-lg mt-4">No activity yet</h3>
        <p className="text-sm text-[#708499] mt-1">
          {(t as any)?.notifications?.feedNotificationsWillAppear ||
            'Your feed notifications will appear here'}
        </p>
      </div>
    );
  }

  const getActionText = (notification: any) => {
    switch (notification.notificationType) {
      case 'view':
        return (t as any)?.notifications?.visitedYou || 'visited you';
      case 'mention':
        return (t as any)?.notifications?.mentionedYou || 'mentioned you';
      case 'upvote':
        return (t as any)?.notifications?.upvotedYourPost || 'upvoted your post';
      case 'comment':
        return (t as any)?.notifications?.commentedOnYourPost || 'commented on your post:';
      default:
        return '';
    }
  };

  const getUserAge = (notification: any) => {
    let age: any = null;
    let partnerAge: any = null;
    if (notification.notificationType === 'view') {
      age = notification.viewer?.age || notification.viewerAge;
      partnerAge = notification.viewer?.partnerAge || notification.viewerPartnerAge;
    } else if (notification.notificationType === 'mention') {
      return null;
    } else if (notification.notificationType === 'upvote') {
      age = notification.voter?.age;
      partnerAge = notification.voter?.partnerAge;
    } else if (notification.notificationType === 'comment') {
      age = notification.commenter?.age;
      partnerAge = notification.commenter?.partnerAge;
    }
    if (age && partnerAge) return `${age}/${partnerAge}`;
    return age;
  };

  const getUserId = (notification: any) => {
    if (notification.notificationType === 'view') return notification.viewer?.id || notification.viewerId;
    if (notification.notificationType === 'mention') return notification.authorId;
    if (notification.notificationType === 'upvote') return notification.voter?.id || notification.voterId;
    if (notification.notificationType === 'comment') return notification.commenter?.id;
    return null;
  };

  const getUsername = (notification: any) => {
    if (notification.notificationType === 'view')
      return notification.viewer?.username || notification.viewerUsername;
    if (notification.notificationType === 'mention') return notification.authorUsername;
    if (notification.notificationType === 'upvote') return notification.voter?.username;
    if (notification.notificationType === 'comment') return notification.commenter?.username;
    return 'Unknown';
  };

  const getPhotoUrl = (notification: any) => {
    if (notification.notificationType === 'view') {
      if (notification.viewer?.photos && notification.viewer.photos.length > 0) {
        return notification.viewer.photos[0]?.url || notification.viewer.photos[0];
      }
      return notification.viewer?.profilePhotoUrl || notification.viewerPhoto;
    }
    if (notification.notificationType === 'mention') return notification.authorPhoto;
    if (notification.notificationType === 'upvote') return notification.voter?.profilePhotoUrl;
    if (notification.notificationType === 'comment') return notification.commenter?.profilePhotoUrl;
    return null;
  };

  const getUserCategory = (notification: any) => {
    if (notification.notificationType === 'view')
      return notification.viewer?.category || notification.viewerCategory;
    if (notification.notificationType === 'mention') return null;
    if (notification.notificationType === 'upvote') return notification.voter?.category;
    if (notification.notificationType === 'comment') return notification.commenter?.category;
    return null;
  };

  return (
    <div className="w-full h-full overflow-y-auto p-3">
      {unifiedNotifications.map((notification, index) => {
        const userId = getUserId(notification);
        const username = getUsername(notification);
        const age = getUserAge(notification);
        const photoUrl = getPhotoUrl(notification);
        const actionText = getActionText(notification);
        const category = getUserCategory(notification);
        const ageColorClass = getCategoryColor(category);

        return (
          <div key={`${notification.notificationType}-${index}-${notification.date.getTime()}`}>
            <div
              className="flex items-center gap-3 py-1.5 px-2 cursor-pointer min-h-[48px]"
              onClick={() => userId && onUserClick(userId)}
            >
              <div className="flex-shrink-0 self-center align-middle h-8 flex items-center justify-center">
                <TelegramImage
                  username={username}
                  userId={userId}
                  alt={username}
                  photoUrl={photoUrl}
                  className="w-8 h-8 rounded-full"
                  variant="avatar"
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-x-2 gap-y-0 flex-nowrap leading-tight overflow-hidden">
                  <VipBadge
                    userId={userId}
                    username={username}
                    size="md"
                    className="font-medium truncate text-sm max-w-[140px]"
                  />
                  {age &&
                    (() => {
                      if (typeof age === 'string' && age.includes('/')) {
                        const [hisAge, herAge] = age.split('/');
                        return (
                          <div className="flex items-center gap-0">
                            <span className="text-blue-500 text-sm font-medium">{hisAge}</span>
                            <span className="text-white/60 text-sm">/</span>
                            <span className="text-pink-400 text-sm font-medium">{herAge}</span>
                          </div>
                        );
                      }
                      return <span className={`${ageColorClass} flex-shrink-0 text-sm`}>{age}</span>;
                    })()}
                  <span className="text-white/60 flex-shrink-0 text-sm">{actionText}</span>
                  <span className="text-white/40 flex-shrink-0 text-xs ml-auto">
                    {formatRelativeTime(notification.date)}
                  </span>
                </div>
                {notification.notificationType === 'comment' && notification.content && (
                  <div className="text-[11px] text-white truncate max-w-full leading-none text-left">
                    {notification.content}
                  </div>
                )}
              </div>
            </div>
            {index < unifiedNotifications.length - 1 && (
              <div className="border-b border-gray-700/30 mx-2"></div>
            )}
          </div>
        );
      })}

      {unifiedNotifications.length >= 20 && (
        <div className="text-center pt-2">
          <span className="text-xs text-gray-500">
            {(t as any)?.notifications?.showingLatestActivities ||
              'Showing the latest 20 activities'}
          </span>
        </div>
      )}
    </div>
  );
}

export default NotificationsFeedPanel;
